import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Typography,
} from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  subscribeToSyncStatus,
  syncOfflineTransactions,
} from '../../services/offlineSyncService';
import { getPendingCount } from '../../utils/offlineDB';

export default function OfflineBanner({ sx = {} }) {
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    const checkQueue = async () => {
      try {
        const count = await getPendingCount();
        setPendingCount(count);
      } catch {}
    };

    checkQueue();

    const unsubscribe = subscribeToSyncStatus((status) => {
      setIsOnline(status.isOnline);
      checkQueue();
    });

    return () => unsubscribe();
  }, []);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await syncOfflineTransactions();
      const count = await getPendingCount();
      setPendingCount(count);
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <Collapse in={!isOnline}>
      <Box sx={{ mb: 2, ...sx }}>
        <Alert
          severity="warning"
          icon={<WifiOffIcon fontSize="inherit" />}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
              disabled={isRetrying}
              sx={{ fontWeight: 700 }}
            >
              {isRetrying ? 'Checking...' : 'Check Connection'}
            </Button>
          }
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.light',
            alignItems: 'center',
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
            Offline Mode Active
          </AlertTitle>
          <Typography variant="body2">
            Network connection is unavailable. Transactions are recorded securely with local encrypted storage
            {pendingCount > 0 ? ` (${pendingCount} queued)` : ''} and will automatically synchronize with the cloud once connection is restored.
          </Typography>
        </Alert>
      </Box>
    </Collapse>
  );
}
